import { pedeReajuste } from '@favo/core';
import type { Cenario, Parametros, Recomendacao } from '@favo/core';

import { CelulaNivel } from '@/componentes/celula-nivel';
import { PainelVeredito } from '@/componentes/painel-veredito';
import { SeloEstado } from '@/componentes/selo-estado';
import {
  formatarData,
  formatarMoeda,
  formatarNumero,
  formatarPercentual,
  SEM_VALOR,
} from '@/design/formato';

import { AcoesDoQueFalta } from './acoes-do-que-falta';
import { veredito } from './veredito';

/** Identificação, veredito e, onde há meta de preço a alcançar, o medidor. */
export function Capa({
  p,
  rec,
  atual,
  empresa,
  nomeProjeto,
  emitidoEm,
}: {
  p: Parametros;
  rec: Recomendacao;
  atual: Cenario | null;
  empresa: string | null;
  nomeProjeto: string | null;
  emitidoEm: string;
}) {
  const v = veredito(p, rec, atual);

  const identificacao = [
    { rotulo: 'Empresa', valor: empresa ?? SEM_VALOR },
    { rotulo: 'Projeto', valor: nomeProjeto ?? SEM_VALOR },
    { rotulo: 'Potência', valor: p.kwp > 0 ? `${formatarNumero(p.kwp)} kWp` : SEM_VALOR },
    {
      rotulo: 'Módulos',
      valor: p.mods > 0 ? `${formatarNumero(p.mods, { casasMax: 0 })} un` : SEM_VALOR,
    },
    { rotulo: 'Custo do kit', valor: p.kit > 0 ? formatarMoeda(p.kit) : SEM_VALOR },
    { rotulo: 'Emitido em', valor: formatarData(emitidoEm) },
    // O laudo expõe despesa fixa, comissão, carga tributária, meta de lucro e
    // lucro anual da empresa. Não é material de cliente, e o documento tem de
    // dizer isso por si: impresso e repassado, ele perde o contexto da tela.
    // Fica na ficha, junto da identificação, e não como faixa de aviso no topo.
    { rotulo: 'Uso', valor: 'Interno. Não enviar ao cliente' },
  ];

  // O medidor de nível só faz sentido onde existe uma meta de preço a
  // alcançar (prejuízo e abaixo). Nos outros estados não há "quanto falta
  // encher".
  const medidor =
    pedeReajuste(rec) ? (
      <CelulaNivel
        fracao={p.ticket / rec.precoSugerido}
        topo={{ rotulo: 'Mínimo para a meta', valor: formatarMoeda(rec.precoSugerido) }}
        falta={{ rotulo: 'Falta', valor: formatarMoeda(rec.reajuste) }}
        nivel={{ rotulo: 'Preço atual', valor: formatarMoeda(p.ticket) }}
        descricao={`O preço atual de ${formatarMoeda(p.ticket)} alcança ${formatarPercentual(
          p.ticket / rec.precoSugerido,
        )} do mínimo de ${formatarMoeda(rec.precoSugerido)}.`}
      />
    ) : null;

  return (
    <section className="capa">
      <header className="capa-topo">
        <div className="min-w-0">
          <span className="rotulo">Relatório de diagnóstico</span>
          <h1 className="titulo-tela">Precificação do projeto</h1>
        </div>
        <SeloEstado tom={v.tom}>{v.selo}</SeloEstado>
      </header>

      <dl className="identificacao">
        {identificacao.map((item) => (
          <div key={item.rotulo}>
            <dt>{item.rotulo}</dt>
            <dd>{item.valor}</dd>
          </div>
        ))}
      </dl>

      <PainelVeredito
        rotulo={v.rotulo}
        cifra={v.cifra}
        {...(v.de === undefined ? {} : { de: v.de })}
        {...(v.para === undefined ? {} : { para: v.para })}
        {...(v.variacao === undefined ? {} : { variacao: v.variacao })}
        explicacao={v.explicacao}
        {...(medidor === null ? {} : { medidor })}
        {...(rec.estado === 'sem-dados' ? { acoes: <AcoesDoQueFalta falta={rec.falta} /> } : {})}
      />
    </section>
  );
}
